import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Receipt,
  FilterList,
  ChevronLeft,
  ChevronRight,
  FilterAltOff,
  Sort,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import TableLoader from "../components/TableLoader";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiFileText,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiSearch,
} from "react-icons/fi";
import { donationsAPI } from "../services/api";
import { colors } from "../theme/modernTheme";
import { useMultiSelect } from "../hooks/useMultiSelect";
import BulkActionToolbar from "../components/BulkActionToolbar";
import BulkDeleteDialog from "../components/BulkDeleteDialog";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { showSuccess, showError } from "../utils/toast";
import { formatDateForDisplay } from "../utils/dateUtils";

export default function Donations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [donations, setDonations] = useState([]);

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [donationToDelete, setDonationToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState("donation_date");
  const [order, setOrder] = useState("desc");

  // Temporary filter states (not applied until user clicks Apply)
  const [donorSearchInput, setDonorSearchInput] = useState("");
  const [recurringInput, setRecurringInput] = useState("");
  const [startDateInput, setStartDateInput] = useState("");
  const [endDateInput, setEndDateInput] = useState("");

  // Applied filter states (used for API calls)
  const [donorSearch, setDonorSearch] = useState("");
  const [recurringFilter, setRecurringFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Multi-select state
  const multiSelect = useMultiSelect(donations, totalCount, "id");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Initialize filters from URL parameters BEFORE first load
  useEffect(() => {
    const typeParam = searchParams.get("type");

    if (typeParam) {
      setRecurringInput(typeParam);
      setRecurringFilter(typeParam);
    }
  }, []);

  useEffect(() => {
    loadDonations();
  }, [
    page,
    rowsPerPage,
    donorSearch,
    recurringFilter,
    startDate,
    endDate,
    orderBy,
    order,
  ]);

  const loadDonations = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order,
      };

      if (donorSearch) params.donor_search = donorSearch;

      // Handle type filter
      if (recurringFilter === "recurring") {
        params.is_recurring = "true";
      } else if (recurringFilter === "pledge") {
        params.is_pledge = "true";
      } else if (recurringFilter === "onetime") {
        params.is_recurring = "false";
        params.is_pledge = "false";
      }

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await donationsAPI.getAll(params);
      setDonations(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/donations/new");
  };

  const handleEdit = (donation) => {
    navigate(`/donations/${donation.id}?mode=edit`);
  };

  const handleRowClick = (donation) => {
    navigate(`/donations/${donation.id}`);
  };

  const handleDeleteClick = (donation) => {
    setDonationToDelete(donation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await donationsAPI.delete(donationToDelete.id);
      multiSelect.removeFromSelection(donationToDelete.id);
      setDeleteDialogOpen(false);
      setDonationToDelete(null);
      loadDonations();
    } catch (error) {
      showError("Failed to delete donation");
    } finally {
      setDeleting(false);
    }
  };

  const handleSuccess = () => {
    loadDonations();
  };

  const handleSendReceipt = async (donation) => {
    try {
      await donationsAPI.sendReceipt(donation.id);
      showSuccess("Receipt sent successfully!");
    } catch (error) {
      const errorMessage = error?.error?.message || "Failed to send receipt";
      showError(errorMessage);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0); // Reset to first page when sorting changes
  };

  const handleApplyFilters = () => {
    setDonorSearch(donorSearchInput);
    setRecurringFilter(recurringInput);
    setStartDate(startDateInput);
    setEndDate(endDateInput);
    setPage(0);
    multiSelect.clearSelection();
  };

  const handleClearFilters = () => {
    setDonorSearchInput("");
    setRecurringInput("");
    setStartDateInput("");
    setEndDateInput("");
    setDonorSearch("");
    setRecurringFilter("");
    setStartDate("");
    setEndDate("");
    setPage(0);
    multiSelect.clearSelection();
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleApplyFilters();
    }
  };

  const hasActiveFilters =
    donorSearchInput || recurringInput || startDateInput || endDateInput;

  // Check if filters have changed from applied state
  const hasFilterChanges =
    donorSearchInput !== donorSearch ||
    recurringInput !== recurringFilter ||
    startDateInput !== startDate ||
    endDateInput !== endDate;

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();

      const filters = {};
      if (donorSearch) filters.donor_search = donorSearch;

      // Handle type filter
      if (recurringFilter === "recurring") {
        filters.is_recurring = "true";
      } else if (recurringFilter === "pledge") {
        filters.is_pledge = "true";
      } else if (recurringFilter === "onetime") {
        filters.is_recurring = "false";
        filters.is_pledge = "false";
      }

      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      await donationsAPI.bulkDelete({
        ...selectionData,
        filters,
      });

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();
      loadDonations();
      showSuccess(
        `Successfully deleted ${multiSelect.selectedCount} donation(s)`
      );
    } catch (error) {
      showError(error?.error?.message || "Failed to delete donations");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Donations are now sorted server-side, no need for client-side sorting
  const sortedDonations = donations;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: "1.5rem",
            fontWeight: 700,
            color: colors.text.primary,
          }}>
          Donations
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={handleAdd}
          sx={{
            borderRadius: "4px",
            backgroundColor: colors.primary.main,
            "&:hover": { backgroundColor: colors.primary.dark },
          }}>
          Add Donation
        </Button>
      </Box>

      {/* Filters and Pagination */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "4px",
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? "none" : "auto",
        }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search donor name"
              value={donorSearchInput}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: donorSearchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDonorSearchInput("");
                      setDonorSearch("");
                      setPage(0);
                      multiSelect.clearSelection();
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: "4px" }}>
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch
                    style={{ marginRight: 8, color: colors.text.secondary }}
                  />
                ),
              }}
              onChange={(e) => setDonorSearchInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Type"
              value={recurringInput}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  sx: {
                    paddingLeft: 0,
                  },
                  PaperProps: {
                    sx: {
                      borderRadius: 0,
                    },
                  },
                },
              }}
              onChange={(e) => setRecurringInput(e.target.value)}>
              <MenuItem value="" sx={{ p: "2px 8px", minHeight: "32px" }}>
                All Types
              </MenuItem>
              <MenuItem
                value="recurring"
                sx={{ p: "2px 8px", minHeight: "32px" }}>
                Recurring
              </MenuItem>
              <MenuItem value="pledge" sx={{ p: "2px 8px", minHeight: "32px" }}>
                Pledge
              </MenuItem>
              <MenuItem
                value="onetime"
                sx={{ p: "2px 8px", minHeight: "32px" }}>
                One-time
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Donation Date From"
              value={startDateInput}
              onChange={(e) => setStartDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: "pointer" }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Donation Date To"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              disabled={multiSelect.selectedCount > 0}
              InputLabelProps={{ shrink: true }}
              onClick={(e) => e.target.showPicker?.()}
              sx={{ cursor: "pointer" }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                size="small"
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "8px",
                  mr: -1,
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Apply
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters || multiSelect.selectedCount > 0}
                sx={{
                  mr: -1,
                  backgroundColor: "transparent",
                  color: colors.primary.main,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 510,
                  p: "8px",
                  "&:disabled": {
                    color: colors.text.disabled,
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}>
                Clear
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}>
              <IconButton
                size="small"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  borderRadius: 1,
                  marginRight: -2,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: "100px",
                  textAlign: "center",
                }}>
                Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setPage(page + 1)}
                disabled={
                  page >= Math.ceil(totalCount / rowsPerPage) - 1 ||
                  multiSelect.selectedCount > 0
                }
                sx={{
                  marginLeft: -2,
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: colors.action.hover,
                  },
                  "&:disabled": {
                    opacity: 0.5,
                    cursor: "not-allowed",
                  },
                }}>
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={true}
        itemName="donations"
      />

      {loading ? (
        <TableLoader message="Loading donations..." />
      ) : sortedDonations.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.text.secondary,
          }}>
          <Typography variant="body1">No donations found</Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "4px",
            boxShadow: "none",
            border: `1px solid ${colors.border.main}`,
          }}>
          <Table>
            <TableHead sx={{ borderBottom: "1px solid #E5E7EB" }}>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  sx={{ width: "2%", py: 1, px: 1 }}>
                  <Checkbox
                    size="medium"
                    indeterminate={multiSelect.isSomeCurrentPageSelected}
                    checked={multiSelect.isAllCurrentPageSelected}
                    onChange={multiSelect.toggleSelectAll}
                    sx={{
                      padding: "4px",
                      color: colors.text.secondary,
                      "& .MuiSvgIcon-root": { fontSize: 28 },
                      transform: "scale(1.2)",
                      "&.Mui-checked": {
                        color: colors.primary.main,
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: colors.primary.main,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    paddingLeft: 0,
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0
                        ? colors.text.disabled
                        : colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    cursor:
                      multiSelect.selectedCount > 0 ? "not-allowed" : "pointer",
                    userSelect: "none",
                    pointerEvents:
                      multiSelect.selectedCount > 0 ? "none" : "auto",
                  }}
                  onClick={() => handleSort("donation_date")}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Donation Date
                    {orderBy === "donation_date" ? (
                      order === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: "250px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Donor
                </TableCell>
                <TableCell
                  sx={{
                    width: "150px",
                    fontWeight: 600,
                    color:
                      multiSelect.selectedCount > 0
                        ? colors.text.disabled
                        : colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    cursor:
                      multiSelect.selectedCount > 0 ? "not-allowed" : "pointer",
                    userSelect: "none",
                    pointerEvents:
                      multiSelect.selectedCount > 0 ? "none" : "auto",
                  }}
                  onClick={() => handleSort("amount")}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    Amount
                    {orderBy === "amount" ? (
                      order === "asc" ? (
                        <ArrowUpward fontSize="small" />
                      ) : (
                        <ArrowDownward fontSize="small" />
                      )
                    ) : (
                      <Sort fontSize="small" sx={{ opacity: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    width: "120px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    width: "150px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  Reminder Status
                </TableCell>
                <TableCell
                  sx={{
                    width: "80px",
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                  {multiSelect.selectedCount > 0 ? "" : "Actions"}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDonations.map((donation) => (
                <TableRow
                  key={donation.id}
                  onClick={() => handleRowClick(donation)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: multiSelect.isSelected(donation)
                      ? colors.primary.main + "10"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: multiSelect.isSelected(donation)
                        ? colors.primary.main + "20"
                        : colors.action.row,
                    },
                    "&:last-child td, &:last-child th": { border: 0 },
                    borderBottom: `1px solid ${colors.border.main}`,
                  }}>
                  <TableCell
                    padding="checkbox"
                    sx={{ py: 1.5, px: 1 }}
                    onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="medium"
                      checked={multiSelect.isSelected(donation)}
                      onChange={(e) => {
                        e.stopPropagation();
                        multiSelect.toggleSelect(donation);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      sx={{
                        padding: "4px",
                        "& .MuiSvgIcon-root": { fontSize: 28 },
                        transform: "scale(1.2)",
                        color: colors.text.secondary,
                        "&.Mui-checked": {
                          color: colors.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      paddingLeft: 0,
                      width: "150px",
                      fontSize: "0.875rem",
                      color: colors.text.primary,
                      py: 1,
                    }}>
                    {donation.donation_date &&
                    donation.donation_date !== "Invalid Date"
                      ? formatDateForDisplay(donation.donation_date)
                      : "Invalid Date"}
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: "160px",
                      fontSize: "0.875rem",
                      color: colors.text.primary,
                      py: 1,
                    }}>
                    <Typography
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "0.875rem",
                      }}>
                      <Tooltip
                        title={
                          donation.contact_type === "INDIVIDUAL"
                            ? `${donation.first_name} ${
                                donation.last_name || ""
                              }`
                            : donation.organization_name
                        }
                        arrow
                        placement="top">
                        <span>
                          {donation.contact_type === "INDIVIDUAL"
                            ? `${donation.first_name} ${
                                donation.last_name || ""
                              }`
                            : donation.organization_name}
                        </span>
                      </Tooltip>
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "100px",
                      fontSize: "0.875rem",
                      color: colors.text.primary,

                      py: 1,
                    }}>
                    {donation.currency === "INR" ? "₹" : "$"}
                    {parseFloat(donation.amount).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: "120px" }}>
                    <Chip
                      label={
                        donation.is_pledge
                          ? "Pledge"
                          : donation.is_recurring
                          ? "Recurring"
                          : "One-time"
                      }
                      size="small"
                      sx={{
                        backgroundColor: donation.is_pledge
                          ? `${colors.warning?.main || "#ff9800"}15`
                          : donation.is_recurring
                          ? `${colors.primary.main}15`
                          : colors.action.hover,
                        color: donation.is_pledge
                          ? colors.warning?.main || "#ff9800"
                          : donation.is_recurring
                          ? colors.primary.main
                          : colors.text.secondary,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        borderRadius: "4px",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1, maxWidth: "120px" }}>
                    {donation.is_recurring && !donation.is_pledge ? (
                      <Chip
                        label={donation.recurring_status || "ACTIVE"}
                        size="small"
                        sx={{
                          justifyContent: "start",
                          textTransform: "capitalize !important",
                          backgroundColor:
                            donation.recurring_status?.toUpperCase() ===
                            "ACTIVE"
                              ? `${colors.success?.main || "#4caf50"}15`
                              : donation.recurring_status?.toUpperCase() ===
                                "PAUSED"
                              ? `${colors.warning?.main || "#ff9800"}15`
                              : donation.recurring_status?.toUpperCase() ===
                                "CANCELLED"
                              ? `${colors.error?.main || "#f44336"}15`
                              : donation.recurring_status?.toUpperCase() ===
                                "COMPLETED"
                              ? `${colors.success?.main || "#4caf50"}15`
                              : donation.recurring_status?.toUpperCase() ===
                                "OVERDUE"
                              ? `${colors.error?.main || "#f44336"}15`
                              : colors.action.hover,
                          color:
                            donation.recurring_status?.toUpperCase() ===
                            "ACTIVE"
                              ? colors.success?.main || "#4caf50"
                              : donation.recurring_status?.toUpperCase() ===
                                "PAUSED"
                              ? colors.warning?.main || "#ff9800"
                              : donation.recurring_status?.toUpperCase() ===
                                "CANCELLED"
                              ? colors.error?.main || "#f44336"
                              : donation.recurring_status?.toUpperCase() ===
                                "COMPLETED"
                              ? colors.success?.main || "#4caf50"
                              : donation.recurring_status?.toUpperCase() ===
                                "OVERDUE"
                              ? colors.error?.main || "#f44336"
                              : colors.text.secondary,
                          fontWeight: 500,
                          fontSize: "0.75rem !important",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          justifyContent: "start",
                          fontSize: "0.875rem",
                          color: colors.text.disabled,
                          textAlign: "center",
                        }}>
                        --
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    sx={{ py: 1 }}>
                    {multiSelect.selectedCount > 0 ? (
                      <Box sx={{ width: "72px" }} />
                    ) : (
                      <Box display="flex" gap={0.5}>
                        {/* <Tooltip title="Send Receipt">
                          <IconButton
                            size="small"
                            onClick={() => handleSendReceipt(donation)}
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}>
                            <FiFileText size={16} />
                          </IconButton>
                        </Tooltip> */}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(donation)}
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.primary.main,
                              },
                            }}>
                            <FiEdit2 size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(donation)}
                            sx={{
                              color: colors.text.primary,
                              p: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                                color: colors.error.main,
                              },
                            }}>
                            <FiTrash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bottom Pagination */}
      {!loading && sortedDonations.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 2,
            gap: 1,
          }}>
          <IconButton
            size="small"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            sx={{
              marginRight: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronLeft />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: colors.text.secondary,
              minWidth: "100px",
              textAlign: "center",
            }}>
            Page {page + 1} of {Math.ceil(totalCount / rowsPerPage) || 1}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
            sx={{
              marginLeft: -2,
              borderRadius: 1,
              "&:hover": {
                backgroundColor: colors.action.hover,
              },
              "&:disabled": {
                opacity: 0.5,
                cursor: "not-allowed",
              },
            }}>
            <FiChevronRight />
          </IconButton>
        </Box>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="donation"
        deleting={deleting}>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: "center" }}>
          This action cannot be undone. The donation and any associated
          reminders will be permanently deleted.
        </Typography>
      </DeleteConfirmationDialog>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="donations"
        deleting={bulkDeleting}>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: "center" }}>
          This action cannot be undone. All selected donations and their
          associated reminders will be permanently deleted.
        </Typography>
      </BulkDeleteDialog>
    </Box>
  );
}
